import { FaEye, FaEyeSlash } from 'react-icons/fa'; // 👁️ Import eye icons
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { resetPassword, verifyResetToken } from '../../api/api';
import CustomAlert from '../../components/CustomAlert';
import { useTranslation } from '../../hooks/useTranslation';
import Footer from '../../components/Footer';

const ResetPassword = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false); // Toggle password visibility
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [isValidToken, setIsValidToken] = useState(null); // null = loading, true = valid, false = invalid
  const [loading, setLoading] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const t = useTranslation();

  const token = new URLSearchParams(location.search).get('token');

  useEffect(() => {
    let isMounted = true;

    const checkToken = async () => {
      try {
        await verifyResetToken(token);
        if (isMounted) setIsValidToken(true);
      } catch (err) {
        if (isMounted) {
          setIsValidToken(false);
          const msgKey = (typeof err === 'object' ? err?.message : err) || 'something_went_wrong';
          setError(t(msgKey));
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    if (token) {
      checkToken();
    } else {
      setIsValidToken(false);
      setError(t('token_is_missing'));
      setLoading(false);
    }

    return () => {
      isMounted = false;
    };
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!password || !confirmPassword) {
      CustomAlert.error('Error', t('all_fields_required'));
      return;
    }
    if (password !== confirmPassword) {
      CustomAlert.error('Error', t('passwords_do_not_match'));
      return;
    }

    try {
      const res = await resetPassword(token, password, confirmPassword);

      CustomAlert.success(t('success'), t(res.message));
      setTimeout(() => navigate('/login', { replace: true }), 2000);
    } catch (err) {
      const msgKey = (typeof err === 'object' ? err?.message : err) || 'something_went_wrong';
      setError(t(msgKey));
    }
  };

  if (loading) {
    return (
      <div
        className="login-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            flex: '1 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div className="login-wrapper">
            <div className="login-image">
              <img src="../../img/RocketBPM_rocket_logo.png" alt="Logo" className="login-logo" />
            </div>
            <p style={{ textAlign: 'center', margin: '20px 0' }}>{t('verifying_link')}</p>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div
        className="login-container"
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          justifyContent: 'space-between',
        }}
      >
        <div
          style={{
            flex: '1 0 auto',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            width: '100%',
          }}
        >
          <div className="login-wrapper">
            <div className="login-image">
              <img src="../../img/RocketBPM_rocket_logo.png" alt="Logo" className="login-logo" />
            </div>
            <h2>{t('invalid_or_expired_link')}</h2>
            <p style={{ color: 'red', textAlign: 'center', margin: '20px 0' }}>{error}</p>
            <button onClick={() => navigate('/forgot-password')} className="login-button">
              {t('request_new_reset_link')}
            </button>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div
      className="login-container"
      style={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        justifyContent: 'space-between',
      }}
    >
      <div
        style={{
          flex: '1 0 auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          width: '100%',
        }}
      >
        <div className="login-wrapper">
          <div className="login-image">
            <img src="../../img/RocketBPM_rocket_logo.png" alt="Logo" className="login-logo" />
          </div>
          <h2>{t('reset_password')}</h2>
          <form onSubmit={handleSubmit} className="login-form">
            <div className="password-field">
              <input
                type={showPassword ? 'text' : 'password'}
                placeholder={t('new_password')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="login-input"
              />
              <span className="eye-icon" onClick={() => setShowPassword(!showPassword)}>
                {showPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>

            <div className="password-field">
              <input
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder={t('confirm_password')}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="login-input"
              />
              <span
                className="eye-icon"
                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
              >
                {showConfirmPassword ? <FaEyeSlash /> : <FaEye />}
              </span>
            </div>
            {error && <p style={{ color: 'red' }}>{error}</p>}
            <button type="submit" className="login-button">
              {t('update_password')}
            </button>
          </form>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPassword;
